<template>
  <div class="grid-module">
    <h2 class="section-title"  @click="som">Payments</h2>
    <div class="grid-module__filter" v-on:keyup.enter="doSearch">
      <b-field class="fil-wrapper"
               :class="{
          'no-custom': (appliedFilter.currentFilter !== 'Custom'),
        }"
               grouped group-multiline>
          <b-field class="search-wrapper">
            <b-input expanded class="input-search"
                     v-model.trim="searchText" type="search">
            </b-input>
            <p class="control">
              <b-button class="primary gobtn" type="is-success"
                    icon-left="magnify" @click="doSearch"></b-button>
            </p>
          </b-field>
        <b-field class="payment-date-wrapper">
          <div class="modern-input-labeled fx-datepicker">
            <label for="startedFilter">Payment Date</label>
            <ModernDateSelect
              ref="modernDateSelect"
              :minDate="paymentDateFilterMin"
              :maxDate="paymentDateFilterMax"
              :options="paymentDateOptions"
              v-model="paymentDateFilter"
            />
          </div>
        </b-field>
        <b-field class="status-wrapper" grouped>
          <span class="label control">Status:</span>
          <b-select expanded class="status-selector"
                    placeholder="Select a name"
                    v-model="status">
            <option
              v-for="statusName in statuses"
              :value="statusName"
              :key="statusName">
              {{ statusName }}
            </option>
          </b-select>
        </b-field>
        <b-field grouped>
        </b-field>
      </b-field>
    </div>
    <div class="grid-module__grid">
      <b-table
        class="payments-tbl"
        :data="data"
        :columns="columns"
        :loading="loading"
        :total="total"
        :per-page="perPage"
        :current-page.sync="page"
        :default-sort="[defaultSort, defaultSortDirection]"
        aria-next-label="Next page"
        aria-previous-label="Previous page"
        aria-page-label="Page"
        aria-current-label="Current page"
        backend-sorting
        paginated
        backend-pagination
        sticky-header
        @page-change="onPageChange"
        @sort="onSort"
        ref="tbl">
        <template slot-scope="props">
          <b-table-column field="amount" class="number_column" numeric>
            <span data-label="Amount" class="loan-applications-table__label"></span>
            <div :style="getRowStyle(props.row.status)">{{ props.row.amount | currency(2) }}</div>
          </b-table-column>

          <b-table-column field="description" >
            <span data-label="Description" class="loan-applications-table__label"></span>
            <div :style="getRowStyle(props.row.status)">{{ props.row.description }}</div>
          </b-table-column>

          <b-table-column field="status" >
            <span data-label="Status" class="loan-applications-table__label"></span>
            <div :style="getStatusColorStyle(props.row.status)">{{ props.row.status }}</div>
          </b-table-column>

          <b-table-column field="createdAt" class="date_column">
            <span data-label="Date" class="loan-applications-table__label"></span>
            <div :style="getRowStyle(props.row.status)">{{ props.row.createdAt }}</div>
          </b-table-column>

          <b-table-column field="failureMessage" >
            <span data-label="Failure Message" class="loan-applications-table__label"></span>
            <div :style="getRowStyle(props.row.status)">{{ props.row.failureMessage }}</div>
          </b-table-column>
        </template>

        <template slot="bottom-left">
          <b-field label="Per page" horizontal style="margin-right: 1rem" class="enh-per-page">
            <b-select v-model="perPage" @input="onPerPageChange" :disabled="isDataEmpty">
              <option
                v-for="perPageOption in perPageOptions"
                :key="perPageOption"
                :value="perPageOption"
                :selected="perPageOption === perPage"
              >{{ perPageOption }}
              </option>
            </b-select>
          </b-field>
        </template>

        <template slot="empty">
          <div v-if="isDataEmpty" class="columns is-centered" style="margin: 0;">
            <div class="column is-half enh-disabled has-text-centered">
              <b-icon pack="fal" icon="frown" size="is-large"></b-icon>
              <p>Nothing here</p>
            </div>
          </div>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import { payments } from '@/api/card_payments';
import { mapGetters, mapState } from 'vuex';
import dayjs from 'dayjs';
import { getRange } from '@/utils/range';
import ModernDateSelect from '@/components/ModernDateSelect.vue';
import { RANGE } from '@/const/constants';

export default {
  name: 'ContractorPayments',
  components: {
    ModernDateSelect,
  },
  computed: {
    ...mapState('user', { userId: (state) => state.id }),
    ...mapState(['contractor']),
    ...mapGetters(['isSwitched', 'userAuthenticator', 'authAuthenticator']),
    applyFilters() {
      return [
        this.status,
        this.appliedFilter.currentFilter,
        this.paymentDateFilter,
      ];
    },
  },

  data() {
    return {
      activeContractor: null,
      paymentDateFilterMin: null,
      paymentDateFilterMax: new Date(),

      paymentDateFilter: { selectedRange: RANGE.THIS_YEAR, ...getRange(RANGE.THIS_YEAR) },

      paymentDateOptions: [
        { value: RANGE.ALL_TIME, label: 'All time' },
        { value: RANGE.LAST_YEAR, label: 'Last year' },
        { value: RANGE.THIS_YEAR, label: 'This year' },
        { value: RANGE.CUSTOM, label: 'Custom' },
      ],
      statuses: [
        '',
        'Succeeded',
        'Failed',
      ],
      status: '',
      columns: [
        {
          field: 'amount',
          label: 'Amount',
          headerClass: 'enh-loan-header',
          sortable: true,
          width: '160',
        },
        {
          field: 'description',
          label: 'Description',
          sortable: true,
          headerClass: 'enh-loan-header',
          width: '500',
        },
        {
          field: 'status',
          label: 'Status',
          sortable: true,
          headerClass: 'enh-loan-header',
        },
        {
          field: 'createdAt',
          label: 'Date',
          sortable: true,
          headerClass: 'enh-loan-header',
        },
        {
          field: 'failureMessage',
          label: 'Failure Message',
          sortable: true,
          headerClass: 'enh-loan-header',
        },
      ],
      perPageOptions: [5, 10, 25, 50],
      isDataEmpty: true,
      data: [],
      total: 0,
      perPage: 5,
      page: 1,
      sortField: 'createdAt',
      sortOrder: 'desc',
      defaultSort: 'createdAt',
      defaultSortDirection: 'desc',
      loading: false,
      appliedFilter: {
        currentFilter: null,
      },
      searchText: '',
    };
  },

  mounted() {
    this.activeContractor = this.contractor.id;
  },

  activated() {
    this.activeContractor = this.contractor.id;
    this.page = 1;
    this.perPage = 5;
    this.$refs.tbl.initSort();
  },
  updated() {
    this.updateLabels();
  },
  watch: {
    activeContractor() {
      this.paymentDateFilter = { selectedRange: RANGE.THIS_YEAR, ...getRange(RANGE.THIS_YEAR) };
      this.paymentDateFilterMin = dayjs(this.contractor.date).startOf('day').toDate();
      this.paymentDateFilterMax = dayjs().toDate();
      this.$refs.modernDateSelect.resetCustomRange();
    },
    applyFilters() {
      this.doSearch();
    },
  },
  methods: {
    som() {
      this.paymentDateFilter = { selectedRange: RANGE.THIS_YEAR };
    },
    getStatusColorStyle(status) {
      switch (status.toLowerCase()) {
        case 'succeeded':
          return 'color: green;';
        case 'failed':
          return 'color: red;';
        case 'upcoming':
          return 'color: black; font-weight: bold;';
        default:
          return 'color: inherit;';
      }
    },

    getRowStyle(status) {
      switch (status.toLowerCase()) {
        case 'upcoming':
          return 'font-weight: bold;';
        default:
          return 'font-weight: inherit;';
      }
    },

    doSearch() {
      this.page = 1;
      this.appliedFilter.searchText = this.searchText;
      this.appliedFilter.status = this.status;
      this.loadAsyncData();
    },
    loadAsyncData() {
      this.loading = true;
      this.isDataEmpty = false;
      const queryParams = {
        order: `${this.sortOrder === 'desc' ? '-' : '+'}${this.sortField}`,
        page: this.page,
        limit: this.perPage,
        userId: this.userId,
        filters: {
          status: this.appliedFilter.status,
          createdAt: {
            start: this.paymentDateFilter.from,
            end: this.paymentDateFilter.to,
          },
          searchText: this.appliedFilter.searchText,
        },
      };
      payments(queryParams).then((response) => {
        this.data = response.data.data;
        this.isDataEmpty = this.data.length === 0;
        this.total = response.data.total;
      })
        .catch((error) => { this.error = error; })
        .finally(() => { this.loading = false; });
    },

    onPageChange(page) {
      this.page = page;
      this.loadAsyncData();
    },

    onPerPageChange() {
      this.onPageChange(1);
    },

    onSort(field, order) {
      this.sortField = field;
      this.sortOrder = order;
      this.loadAsyncData();
    },

    updateLabels() {
      const elms = document.querySelectorAll('span.loan-applications-table__label');
      elms.forEach((elm) => {
        elm.parentElement.setAttribute('data-label', elm.getAttribute('data-label'));
      });
    },

    dateToString(date) {
      return date.toLocaleString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric' });
    },

    dateParse(date) {
      return new Date(date);
    },
  },
};
</script>

<style scoped lang="stylus">
.enhancify.grid-module.grid-module__filter.datepicker
  min-width 230px
.field.selector-wrapper.has-addons
  min-width 230px
.payments-tbl >>> .table
  border: 1px solid #80808059
  thead > tr > th.enh-loan-header
    height 90px
.grid-module > h2.section-title
  margin-top 30px
  margin-bottom 0
.b-table >>> .enh-loan-header
  font-size 18px!important
  font-weight 500!important
.grid-module
  height auto

.grid-module__grid
  overflow visible

.number_column
  width 100px
.date_column
  width 150px

.field.date-filter
  justify-content start
.field.date-filter >>> div.field.selector-wrapper.has-addons
  min-width 230px
.field.date-filter >>> .datepicker.control
  min-width 230px
.field.is-grouped.is-grouped-multiline:last-child
  margin-bottom 13px
.field.fil-wrapper
  column-gap 30px
@media screen and (max-width: 768px)
  .field.search-wrapper, .payment-date-wrapper, .status-wrapper
    width 100%
    margin-right 0!important
  .field.status-wrapper > .status-selector
    width 100%

</style>
